import React, { useState } from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

interface ProductImageWithHoverProps {
  imageUrl?: string;
  productSku: string;
  size?: "small" | "medium" | "large";
}

const ProductImageWithHover: React.FC<ProductImageWithHoverProps> = ({
  imageUrl,
  productSku,
  size = "small"
}) => {
  const thumbnailSize = size === "large" ? "w-12 h-12" : size === "medium" ? "w-8 h-8" : "w-5 h-5";
  // Controlled so the preview opens only on a user gesture (see the trigger).
  const [open, setOpen] = useState(false);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.src = "/placeholder.svg";
  };

  if (!imageUrl) {
    return (
      <div className={`${thumbnailSize} rounded border border-border bg-muted flex items-center justify-center`}>
        <span className="text-[8px] text-muted-foreground">-</span>
      </div>
    );
  }

  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={200} closeDelay={100}>
      <HoverCardTrigger
        asChild
        // BLU-1302: Radix opens the card on ANY focus, including a script's.
        // A dialog auto-focuses its first tabbable element on open, and in
        // the inventory product-detail modal that is this thumbnail, so the
        // preview popped by itself every time a SKU was opened.
        // preventDefault skips Radix's open-on-focus (its handler is composed
        // with composeEventHandlers); hover still opens it as before.
        onFocus={(e) => e.preventDefault()}
        // Keyboard users still get the preview when they TAB onto the
        // thumbnail: the Tab keyup lands on the newly focused element, while
        // a dialog's auto-focus has no keyup (or an Enter keyup) here.
        onKeyUp={(e) => {
          if (e.key === "Tab") setOpen(true);
        }}
      >
        {/* Focusable so the preview is reachable by keyboard. Deliberately
            NOT a <button>: this thumbnail sits inside clickable rows on a
            dozen surfaces, and a button would swallow the row's own click. */}
        <div
          tabIndex={0}
          role="img"
          aria-label={productSku}
          className={`${thumbnailSize} rounded overflow-hidden border border-border cursor-pointer transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1`}
        >
          <img
            src={imageUrl}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover"
            loading="lazy"
            onError={handleImageError}
          />
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-auto p-2" side="right" align="center">
        <div className="w-[200px] h-[200px] rounded overflow-hidden">
          <img
            src={imageUrl}
            alt={productSku}
            className="w-full h-full object-contain bg-white"
            onError={handleImageError}
          />
        </div>
        <p className="text-xs text-muted-foreground text-center mt-2">{productSku}</p>
      </HoverCardContent>
    </HoverCard>
  );
};

// Memoized: the bulk stock editor renders up to 200 of these inside a form
// whose row list rebuilds on every keystroke. Props are primitives, so a
// shallow compare keeps the thumbnails out of that render path entirely.
export default React.memo(ProductImageWithHover);